<template>
  <SubHeading :home="home" :tabs="[]" :currTab="home" @active="handleHome" />

  <nav class="sba-collection-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text">
          Activity
        </span>
        <span class="sba-collection-context">Internal Activity Feed</span>
      </div>
    </div>
  </nav>

  <div class="sba-collection-page-surface is-workbench activity-page-surface">
    <div class="container">
      <LoadingDiv v-if="isLoading && !activityPages" />

      <div class="activity-workbench sba-slide-in" v-else>
        <section class="activity-leader">
          <div>
            <span class="activity-eyebrow">Activity Bell</span>
            <h4>Team Activity</h4>
            <p>
              Review logged actions from the modules available to you. Today's count resets naturally each day.
            </p>
          </div>
          <span class="activity-today-chip">
            <i class="material-icons">notifications_active</i>
            {{ todayCount }} today
          </span>
        </section>

        <section class="activity-log-card">
          <div class="activity-log-head">
            <div>
              <span class="activity-eyebrow">Latest First</span>
              <h5>Activity Updates</h5>
            </div>
            <span>{{ activityPages?.total || 0 }} records</span>
          </div>

          <form class="activity-log-filters" @submit.prevent="handleSearch">
            <label>
              <span>Search activity</span>
              <input
                v-model.trim="activitySearch"
                type="search"
                placeholder="Provider, client, user, tariff..."
                autocomplete="off"
              />
            </label>
            <label>
              <span>Group</span>
              <select v-model="activityGroup" class="browser-default">
                <option value="all">All groups</option>
                <option v-for="group in allowedGroups" :key="group" :value="group">
                  {{ groupLabel(group) }}
                </option>
              </select>
            </label>
            <label>
              <span>Severity</span>
              <select v-model="activitySeverity" class="browser-default">
                <option value="all">All levels</option>
                <option value="critical">Critical</option>
                <option value="important">Important</option>
                <option value="notice">Notice</option>
                <option value="info">Info</option>
              </select>
            </label>
            <label>
              <span>Time</span>
              <select v-model="activityTime" class="browser-default">
                <option value="all">All time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 days</option>
                <option value="month">Last 30 days</option>
              </select>
            </label>
            <button class="activity-log-search" type="submit" :disabled="isLoading">
              <i class="material-icons">{{ isLoading ? "hourglass_empty" : "search" }}</i>
              <span>{{ isLoading ? "Loading" : "Search" }}</span>
            </button>
          </form>

          <div class="progress activity-log-progress" v-if="isLoading">
            <div class="indeterminate"></div>
          </div>

          <div class="activity-log-message is-error" v-if="error">
            {{ error }}
          </div>

          <div class="activity-log-message" v-else-if="!isEnabled && !isLoading">
            Activity feed is not available for your current modules.
          </div>

          <div class="activity-log-message" v-else-if="!activityRows.length && !isLoading">
            No activity logs found for the current filters.
          </div>

          <div
            v-for="item in activityRows"
            :key="item.id"
            class="activity-log-row"
            :class="`activity-log-row-${activityTone(item)}`"
          >
            <i class="material-icons">{{ activityIcon(item.event_group) }}</i>
            <div>
              <strong>{{ item.title || "Activity update" }}</strong>
              <small>{{ item.display_summary || item.summary || item.event_key }}</small>
            </div>
            <div class="activity-log-meta">
              <span>{{ groupLabel(item.event_group) }}</span>
              <em>{{ formatActivityTime(item.created_at) }}</em>
            </div>
          </div>

          <div class="activity-log-more" v-if="hasMoreActivity">
            <button
              type="button"
              class="activity-log-load-more"
              :disabled="isLoadingMore"
              @click="loadMoreActivity"
            >
              <i class="material-icons">{{ isLoadingMore ? "hourglass_empty" : "expand_more" }}</i>
              <span>{{ isLoadingMore ? "Loading" : "Load more" }}</span>
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import getItems from "@/composables/getItems";

export default {
  name: "activityHome",
  components: { SubHeading, LoadingDiv },
  setup() {
    const home = ref({ id: 1, name: "Activity", title: "Activity", url: "activity" });
    const isEnabled = ref(false);
    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const error = ref(null);
    const todayCount = ref(0);
    const allowedGroups = ref([]);
    const activityLogs = ref([]);
    const activityPages = ref(null);
    const activitySearch = ref("");
    const activityTime = ref("all");
    const activityGroup = ref("all");
    const activitySeverity = ref("all");
    const activityPage = ref(1);

    const activityRows = computed(() => activityLogs.value);

    const hasMoreActivity = computed(() => {
      if (!activityPages.value) {
        return false;
      }

      return activityPages.value.current_page < activityPages.value.last_page;
    });

    onMounted(() => {
      document.title = "Activity";
      loadActivitySummary();
      loadActivityLogs();
    });

    const handleHome = () => {
      activityPage.value = 1;
      loadActivityLogs();
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const buildActivityUrl = (page = 1) => {
      const params = ["per_page=25", `page=${page}`];

      if (activitySearch.value) {
        params.push(`q=${encodeURIComponent(activitySearch.value)}`);
      }

      if (activityTime.value) {
        params.push(`time=${activityTime.value}`);
      }

      if (activityGroup.value) {
        params.push(`group=${activityGroup.value}`);
      }

      if (activitySeverity.value) {
        params.push(`severity=${activitySeverity.value}`);
      }

      return `activity/bell?${params.join("&")}`;
    };

    const loadActivitySummary = async () => {
      const { res, err } = await getItems("activity/bell/summary");

      if (!err.value) {
        const payload = res.value?.data || {};
        isEnabled.value = !!payload.enabled;
        todayCount.value = payload.today_count || 0;
        allowedGroups.value = payload.groups || [];
      }
    };

    const loadActivityLogs = async ({ page = 1, append = false } = {}) => {
      if (append) {
        isLoadingMore.value = true;
      } else {
        isLoading.value = true;
      }

      error.value = null;

      const { res, err } = await getItems(buildActivityUrl(page));

      if (err.value) {
        error.value = err.value;
      } else {
        const payload = res.value?.data || {};
        isEnabled.value = !!payload.enabled;
        allowedGroups.value = payload.groups || [];
        activityPages.value = payload.logs;
        activityLogs.value = append
          ? [...activityLogs.value, ...(payload.logs?.data || [])]
          : payload.logs?.data || [];
        activityPage.value = payload.logs?.current_page || page;
      }

      isLoading.value = false;
      isLoadingMore.value = false;
    };

    const handleSearch = () => {
      activityPage.value = 1;
      loadActivityLogs();
    };

    const loadMoreActivity = () => {
      if (!hasMoreActivity.value || isLoadingMore.value) {
        return;
      }

      loadActivityLogs({
        page: activityPage.value + 1,
        append: true,
      });
    };

    const activityIcon = (group) => {
      const icons = {
        auth: "insert_comment",
        provider: "local_hospital",
        tariff: "receipt_long",
        schedule: "view_timeline",
        client: "business",
        policy: "event_available",
        plan: "inventory_2",
        principal: "person",
        member: "badge",
        claim_batch: "fact_check",
        invoice: "request_quote",
        payment: "payments",
        repayment: "currency_exchange",
      };

      return icons[group] || "bolt";
    };

    const activityTone = (item) => {
      if (item.severity == "critical") {
        return "warning";
      }

      const tones = {
        auth: "auth",
        provider: "provider",
        tariff: "tariff",
        schedule: "tariff",
        client: "client",
        policy: "client",
        plan: "client",
        principal: "client",
        member: "client",
        claim_batch: "claim",
        invoice: "invoice",
        payment: "payment",
        repayment: "payment",
      };

      return tones[item.event_group] || "default";
    };

    const groupLabel = (group) => {
      const labels = {
        auth: "Pre-Auth",
        provider: "Provider",
        tariff: "Tariff",
        schedule: "Schedule",
        client: "Client",
        policy: "Policy",
        plan: "Plan",
        principal: "Principal",
        member: "Member",
        claim_batch: "Claims",
        invoice: "Invoices",
        payment: "Payments",
        repayment: "Repayments",
      };

      return labels[group] || group;
    };

    const formatActivityTime = (value) => {
      if (!value) {
        return "";
      }

      return new Date(value).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    return {
      home,
      isEnabled,
      isLoading,
      isLoadingMore,
      error,
      todayCount,
      allowedGroups,
      activityRows,
      activityPages,
      activitySearch,
      activityTime,
      activityGroup,
      activitySeverity,
      hasMoreActivity,
      handleHome,
      handleSearch,
      loadMoreActivity,
      activityIcon,
      activityTone,
      groupLabel,
      formatActivityTime,
    };
  },
};
</script>

<style scoped>
.activity-page-surface {
  min-height: calc(100vh - 180px);
  padding-bottom: 72px;
}

.activity-workbench {
  display: grid;
  gap: 16px;
}

.activity-leader,
.activity-log-card {
  background: #ffffff;
  border: 1px solid #c7d7dd;
  border-radius: 6px;
  box-shadow: 0 18px 36px -36px rgba(38, 50, 56, 0.72);
  overflow: hidden;
}

.activity-leader {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 24px 28px;
}

.activity-eyebrow {
  color: #00897b;
  display: block;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  margin-bottom: 8px;
}

.activity-leader h4 {
  color: #263238;
  font-size: 1.7rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.activity-leader p {
  color: #607d8b;
  font-size: 0.98rem;
  line-height: 1.5;
  margin: 7px 0 0;
  max-width: 720px;
}

.activity-today-chip {
  align-items: center;
  background: #fff8e1;
  border: 1px solid #ffd54f;
  border-radius: 999px;
  color: #5d4037;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.84rem;
  font-weight: 900;
  gap: 8px;
  padding: 10px 14px;
}

.activity-today-chip i {
  color: #00897b;
  font-size: 18px;
}

.activity-log-head {
  align-items: center;
  background: #fbfcfc;
  border-bottom: 1px solid #dfe6e8;
  display: flex;
  justify-content: space-between;
  padding: 18px 20px;
}

.activity-log-head h5 {
  color: #263238;
  font-size: 1.16rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.activity-log-head > span {
  color: #607d8b;
  font-size: 0.82rem;
  font-weight: 800;
}

.activity-log-filters {
  align-items: end;
  border-bottom: 1px solid #dfe6e8;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(190px, 1.4fr) minmax(130px, 0.8fr) minmax(130px, 0.8fr) minmax(130px, 0.8fr) auto;
  padding: 14px 18px;
}

.activity-log-filters label {
  color: #455a64;
  display: grid;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.activity-log-filters input,
.activity-log-filters select {
  background: #ffffff;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  box-sizing: border-box;
  color: #263238;
  font-size: 0.92rem;
  height: 38px;
  margin: 0;
  padding: 0 11px;
}

.activity-log-filters input:focus,
.activity-log-filters select:focus {
  border-color: #263238;
  box-shadow: none;
  outline: 0;
}

.activity-log-search,
.activity-log-load-more {
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  font-weight: 800;
  gap: 7px;
  justify-content: center;
}

.activity-log-search {
  background: #263238;
  border: 1px solid #263238;
  border-radius: 6px;
  color: #ffffff;
  font-size: 0.82rem;
  height: 38px;
  padding: 0 14px;
}

.activity-log-search:disabled,
.activity-log-load-more:disabled {
  cursor: wait;
  opacity: 0.7;
}

.activity-log-search i,
.activity-log-load-more i {
  font-size: 18px;
}

.activity-log-progress {
  background: #edf2f4;
  border-radius: 0;
  height: 3px;
  margin: 0;
}

.activity-log-message {
  color: #607d8b;
  font-size: 0.9rem;
  font-weight: 700;
  padding: 18px;
}

.activity-log-message.is-error {
  color: #b71c1c;
}

.activity-log-row {
  align-items: center;
  border-bottom: 1px solid #edf1f3;
  display: grid;
  gap: 14px;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  padding: 15px 20px;
}

.activity-log-row:last-child {
  border-bottom: 0;
}

.activity-log-row > i {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dce5e8;
  border-radius: 6px;
  color: #00897b;
  display: inline-flex;
  font-size: 19px;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.activity-log-row strong,
.activity-log-row small {
  display: block;
}

.activity-log-row strong {
  color: #263238;
  font-size: 0.96rem;
  font-weight: 800;
}

.activity-log-row small {
  color: #607d8b;
  font-size: 0.9rem;
  line-height: 1.42;
  margin-top: 4px;
}

.activity-log-meta {
  align-items: flex-end;
  display: grid;
  gap: 7px;
  justify-items: end;
}

.activity-log-meta span,
.activity-log-meta em {
  border: 1px solid #dfe6e8;
  border-radius: 999px;
  color: #607d8b;
  font-size: 0.74rem;
  font-style: normal;
  font-weight: 800;
  padding: 6px 10px;
  white-space: nowrap;
}

.activity-log-meta span {
  background: #f6f8f9;
}

.activity-log-row-auth > i,
.activity-log-row-auth .activity-log-meta span {
  background: #e0f2f1;
  border-color: #b2dfdb;
  color: #00695c;
}

.activity-log-row-provider > i,
.activity-log-row-provider .activity-log-meta span {
  background: #e3f2fd;
  border-color: #bbdefb;
  color: #1565c0;
}

.activity-log-row-tariff > i,
.activity-log-row-tariff .activity-log-meta span {
  background: #fff8e1;
  border-color: #ffe082;
  color: #8a5a00;
}

.activity-log-row-client > i,
.activity-log-row-client .activity-log-meta span {
  background: #e8f5e9;
  border-color: #c8e6c9;
  color: #2e7d32;
}

.activity-log-row-claim > i,
.activity-log-row-claim .activity-log-meta span {
  background: #ede7f6;
  border-color: #d1c4e9;
  color: #512da8;
}

.activity-log-row-invoice > i,
.activity-log-row-invoice .activity-log-meta span {
  background: #fff3e0;
  border-color: #ffcc80;
  color: #e65100;
}

.activity-log-row-payment > i,
.activity-log-row-payment .activity-log-meta span {
  background: #e8eaf6;
  border-color: #c5cae9;
  color: #303f9f;
}

.activity-log-row-warning > i,
.activity-log-row-warning .activity-log-meta span {
  background: #ffebee;
  border-color: #ffcdd2;
  color: #b71c1c;
}

.activity-log-more {
  border-top: 1px solid #dfe6e8;
  display: flex;
  justify-content: center;
  padding: 14px 18px 16px;
}

.activity-log-load-more {
  background: #ffffff;
  border: 1px solid #cfd8dc;
  border-radius: 999px;
  color: #263238;
  font-size: 0.84rem;
  min-height: 36px;
  padding: 0 16px;
}

.activity-log-load-more:hover {
  border-color: #263238;
}

@media only screen and (max-width: 992px) {
  .activity-leader {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .activity-log-filters,
  .activity-log-row {
    grid-template-columns: 1fr;
  }

  .activity-log-meta {
    align-items: flex-start;
    justify-items: start;
  }
}
</style>
